import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Play, Target, Trophy } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordDiagram } from '@/components/ChordDiagram';
import { ModeCard } from '@/components/ModeCard';
import { ProgressRing } from '@/components/ProgressRing';
import { Button, Card, SectionTitle } from '@/components/ui';
import { minutesLabel, t } from '@/i18n';
import { useProgress } from '@/hooks/useProgress';
import { getChord } from '@/data/chords';
import { formatTransitionKey } from '@/training/summary';
import { MODE_CONFIG } from '@/components/modeConfig';
import './HomePage.css';

/**
 * Home: one primary action and nothing that competes with it.
 *
 * The headline card always shows the *next* recommended session, so a player
 * who does not want to think can open the app, press one button and practise.
 */
export function HomePage() {
  const navigate = useNavigate();
  const { sessions, transitions, recommendation, loading } = useProgress();

  const stats = useMemo(() => summarise(sessions), [sessions]);
  const bestTransition = useMemo(() => pickBest(transitions), [transitions]);

  const headline =
    recommendation.chordIds.length >= 2
      ? `${recommendation.chordIds[0]} → ${recommendation.chordIds[1]}`
      : (recommendation.chordIds[0] ?? t('modes.rhythm'));

  const goal = recommendation.transitionKey
    ? transitions.find((item) => item.key === recommendation.transitionKey)
    : undefined;
  const readiness = goal ? Math.round(goal.successRate * 100) : 0;

  const startPath = MODE_CONFIG[recommendation.mode].to;

  return (
    <AppShell>
      <section className="page-section">
        <SectionTitle>{t('home.today')}</SectionTitle>
        {/* No ornament here on purpose: the hero already carries a headline,
            two diagrams, a reason and the primary action, and a silhouette
            behind any of them would break the "never under text" rule. */}
        <Card section className="home-hero">
          <div className="home-hero__headline">{headline}</div>
          <p className="home-hero__meta">
            {recommendation.mode === 'cleanChord'
              ? minutesLabel(recommendation.durationMin)
              : `${minutesLabel(recommendation.durationMin)} • ${recommendation.bpm} BPM`}
          </p>

          {/* A readiness ring means nothing before there is anything to
              measure. A brand-new player gets the two shapes to put their
              fingers on instead, which is the thing they actually need. */}
          {goal ? (
            <div className="home-hero__ring">
              <ProgressRing value={readiness} size={150} label={`${readiness}%`} />
            </div>
          ) : (
            <div className="home-hero__shapes">
              {recommendation.chordIds.slice(0, 2).map((chordId) => {
                const chord = getChord(chordId);
                return chord ? <ChordDiagram key={chordId} chord={chord} size={120} /> : null;
              })}
            </div>
          )}

          <p className="home-hero__reason">{recommendation.explanationRu}</p>

          <Button large block onClick={() => navigate(startPath, { state: { recommendation } })}>
            <Play size={20} fill="currentColor" aria-hidden="true" />
            {t('home.start')}
          </Button>
        </Card>
      </section>

      <section className="page-section">
        <SectionTitle>{t('home.modes')}</SectionTitle>
        <div className="stack-12">
          <ModeCard mode="chordChange" />
          <ModeCard mode="cleanChord" />
          <ModeCard mode="rhythm" />
        </div>
      </section>

      <section className="page-section">
        <SectionTitle>{t('home.progress')}</SectionTitle>
        <Card padded={false}>
          <div className="home-stats">
            <StatCell
              icon={<Target size={20} aria-hidden="true" />}
              tone="green"
              label={t('home.accuracy')}
              value={loading || stats.accuracy === null ? '—' : `${stats.accuracy}%`}
            />
            <StatCell
              icon={<Clock size={20} aria-hidden="true" />}
              tone="blue"
              label={t('home.avgTime')}
              value={
                stats.avgTransitionSec === null
                  ? '—'
                  : `${stats.avgTransitionSec.toFixed(2)} ${t('home.seconds')}`
              }
            />
            <StatCell
              icon={<Trophy size={20} aria-hidden="true" />}
              tone="amber"
              label={t('home.bestTransition')}
              value={bestTransition ? formatTransitionKey(bestTransition.key) : '—'}
              accent
            />
          </div>
        </Card>
      </section>
    </AppShell>
  );
}

interface StatCellProps {
  icon: React.ReactNode;
  tone: 'green' | 'blue' | 'amber';
  label: string;
  value: string;
  accent?: boolean;
}

function StatCell({ icon, tone, label, value, accent = false }: StatCellProps) {
  return (
    <div className="home-stat">
      <span className={`home-stat__icon home-stat__icon--${tone}`}>{icon}</span>
      <span className="home-stat__body">
        <span className="home-stat__label">{label}</span>
        <span className={`home-stat__value tnum${accent ? ' home-stat__value--accent' : ''}`}>{value}</span>
      </span>
    </div>
  );
}

function summarise(sessions: { accuracy: number; avgTransitionSec: number | null }[]) {
  if (sessions.length === 0) return { accuracy: null as number | null, avgTransitionSec: null as number | null };
  const recent = sessions.slice(0, 7);
  const accuracy = Math.round(recent.reduce((sum, s) => sum + s.accuracy, 0) / recent.length);
  const times = recent.map((s) => s.avgTransitionSec).filter((v): v is number => v !== null);
  return {
    accuracy,
    avgTransitionSec: times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : null,
  };
}

function pickBest<T extends { bestSec: number | null; key: string }>(items: T[]): T | undefined {
  return items
    .filter((item) => item.bestSec !== null)
    .sort((a, b) => (a.bestSec ?? Infinity) - (b.bestSec ?? Infinity))[0];
}
